<script lang="ts">
    import Fa from 'svelte-fa'
    import { faEdit, faPlusCircle, faMinusCircle, faUser, faPlug, faRobot } from '@fortawesome/free-solid-svg-icons'
    import { onMount } from 'svelte/internal'
    import Content from '../../_common/content.svelte'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import DateSelect from '../../_common/date-select.svelte'
    import Popover from '../../_common/popover.svelte'
    import { GridBadgeClassEnum, GridButton, GridClass, GridCol, GridColTypeEnum, GridLine } from '../../_common/z/zgrid'
    import Zgrid from '../../_common/z/zgrid.svelte'
    import type { BillEntity } from '../../../modules/financialApi/bills/bill'
    import dayjs from 'dayjs'
    import _isToday from 'dayjs/plugin/isToday'
    import { EntryTypeEnum } from '../../../modules/financialApi/entries/entry.enum'
    import Financial_4AccountsImport from './financial-4-accounts-import.svelte'
    import type { Entry } from '../../../modules/financialApi/entries/entry'
    import Financial_4AccountsForm from './financial-4-accounts-form.svelte'
    import AccountSelect from '../../financial/accounts/account-select.svelte'
    import type { Account } from '../../../modules/financialApi/accounts/account'
    import Financial_4AccountsClassification from './financial-4-accounts-classification.svelte'
    import Zact from '../../_common/z/zact.svelte'

    dayjs.extend(_isToday)

    let show = false
    let entities: Entry[] = []
    let total = 0

    let account: Account = null

    let dates: { from: Date; to: Date } = null
    dates = window.api.utils.dates.currentMonth()

    onMount(async () => {
        await loadEntities()
    })

    function dateChanged(event) {
        dates = event.detail
        loadEntities()
    }

    async function loadEntities() {
        try {
            show = true
            total = 0
            const dt = {
                from: dates?.from || undefined,
                to: dates?.to || undefined,
                accountId: !account ? undefined : account.accountId,
            }
            entities = await window.api.financials.entries.getAll(dt)
            entities = await window.api.utils.utils.assignPersonsFromObject(entities)
            console.log('financial.entries', entities)
            total = entities.reduce((total, item) => {
                total += item.value
                return total
            }, 0)
            loadGrid()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    function evaluateBadge(item: BillEntity): { label: string; class: string } {
        if (item.status === 100) {
            return {
                label: 'pago',
                class: GridBadgeClassEnum.badge_success,
            }
        }
        const isToday = dayjs(item.dueAt).isToday()
        if (isToday) {
            return {
                label: 'vence hoje',
                class: GridBadgeClassEnum.badge_alert,
            }
        }
        if (dayjs(item.dueAt).diff(new Date(), 'day') >= 0) {
            return {
                label: 'a pagar',
                class: GridBadgeClassEnum.badge_default,
            }
        }
        return {
            label: 'atrasado',
            class: GridBadgeClassEnum.badge_danger,
        }
    }

    let grid: GridClass = new GridClass()
    function loadGrid() {
        // grid
        grid = new GridClass()
        // grid head
        grid.title = 'Movimentação'
        grid.subtitle = 'Movimentações financeiras efetivadas através de contas bancárias, cartões de crédito e dinheiro.'
        grid.fontSize = '15px'
        grid.cols.push(new GridCol({ content: 'Data', type: GridColTypeEnum.date }))
        if (window.configs.financialShowAccrual) {
            grid.cols.push(new GridCol({ content: 'Competência', type: GridColTypeEnum.string }))
        }
        grid.cols.push(new GridCol({ content: 'Conta', type: GridColTypeEnum.string, maxLength: 30 }))
        grid.cols.push(new GridCol({ content: 'Descrição', type: GridColTypeEnum.string, maxLength: 35 }))
        grid.cols.push(new GridCol({ content: 'Credor/pagador', type: GridColTypeEnum.string, maxLength: 35 }))
        grid.cols.push(new GridCol({ content: 'Categoria', type: GridColTypeEnum.string, maxLength: 40 }))
        grid.cols.push(new GridCol({ content: 'Valor', type: GridColTypeEnum.float_2, showTotal: true }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        // grid lines
        for (const item of entities) {
            const btnPerson = new GridButton({
                callback: () => window.person('show', item.personId),
                icon: faUser,
                label: '',
                disabled: !item.personId,
            })
            const btnOpen = new GridButton({
                callback: () => goshowForm(item.entryId, item.type),
                icon: faEdit,
                label: 'Editar',
            })
            let line: GridLine = new GridLine()
            line.slots.push({ content: item.paymentAt })
            if (window.configs.financialShowAccrual) {
                line.slots.push({ content: item.accrualMonth ? `${item.accrualMonth}/${item.accrualYear}` : item.accrualYear })
            }
            line.slots.push({ content: item.account?.label })
            line.slots.push({ content: item.description })
            line.slots.push({ content: item.person?.name || '-' })
            line.slots.push({ content: item.category?.label || '-' })
            line.slots.push({ content: item.type === EntryTypeEnum.credit ? item.value : item.value * -1 })
            line.slots.push({ content: [btnOpen] })
            grid.lines.push(line)
        }
    }

    let entryId = null
    let showForm = false
    let creditDebit = null
    function goshowForm(id: string, _creditDebit: EntryTypeEnum) {
        creditDebit = _creditDebit
        entryId = id
        showForm = true
    }

    let showImport = false
    function goshowImport() {
        showImport = true
    }

    function goNew(_creditDebit: EntryTypeEnum) {
        creditDebit = _creditDebit
        entryId = null
        showForm = true
    }

    function goClose(update: boolean) {
        showForm = false
        showImport = false
        entryId = null
        if (update) loadEntities()
    }

    let showClassification = false
    function goShowClassification() {
        showClassification = true
    }
</script>

{#if showClassification}
    <Popover title="Classificação automática" width="1300px" height="90vh" on:close={() => (showClassification = false)}>
        <Financial_4AccountsClassification on:close={() => (showClassification = false)}></Financial_4AccountsClassification>
    </Popover>
{/if}

{#if showForm}
    <Popover title="Novo lançamento" width="970px" on:close={() => goClose(true)}>
        <Financial_4AccountsForm {entryId} {creditDebit} on:change={() => goClose(true)}></Financial_4AccountsForm>
    </Popover>
{/if}

{#if showImport}
    <Popover title="Importar" width="1300px" height="90vh" on:close={() => goClose(true)}>
        <Financial_4AccountsImport on:change={() => goClose(true)}></Financial_4AccountsImport>
    </Popover>
{/if}

<Content footer={true}>
    <Zact>
        <div class="fix-width">
            <AccountSelect all={true} label="Conta" bind:account on:change={loadEntities} />
        </div>
        <DateSelect startFrom={dates.from} startTo={dates.to} on:change={dateChanged} />
        <div class="spacer h-mobile" />
        <button class="btn btn-sm btn-secondary px-1" on:click={goshowImport}><Fa icon={faPlug} /> Importação</button>
        <button class="btn btn-sm btn-primary px-1" on:click={() => goNew(EntryTypeEnum.credit)}><Fa icon={faPlusCircle} /> Entrada</button>
        <button class="btn btn-sm btn-primary px-1" on:click={() => goNew(EntryTypeEnum.debit)}><Fa icon={faMinusCircle} /> Saída</button>
    </Zact>
    <Zgrid {grid} class="h-space" />
    <LoadSpinner {show} />
</Content>

<style>
    .fix-width {
        max-width: 280px;
    }
</style>
